import Box from '@mui/material/Box';
import { transparentize } from 'color2k';
import { Colors } from 'styles/index';
import { ScreenName, type WorkspaceLayoutPreset } from 'generated/graphql';
import { type SxProps, type Theme } from '@mui/material/styles';
import useResizeAware from 'react-resize-aware';
import { resolveRelativeRect } from '../utils';
import {
  getArrangementBounds,
  getCanvasDimensions,
  getDisplayGeometry,
  getWindowColor,
  getWindowLabel,
  getWindowName,
  groupWindowsByDisplayIndex,
  type DisplayGeometry,
} from './WorkspaceLayoutPreview.utils';

const CANVAS_MIN_WIDTH = 180;
const CANVAS_VERTICAL_PADDING = 16;
const DISPLAY_GAP = 12;

type WorkspaceLayoutPreviewProps = {
  workspaceLayoutPreset: Pick<WorkspaceLayoutPreset, 'displaySlots' | 'windows'>;
};

const outerContainerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

const displayContainerSx = (canvasWidth: number, canvasHeight: number): SxProps<Theme> => ({
  position: 'relative',
  width: canvasWidth,
  maxWidth: '100%',
  height: canvasHeight,
  mx: 'auto',
  overflow: 'hidden',
});

const displaySx = ({ left, top, width, height }: DisplayGeometry): SxProps<Theme> => ({
  position: 'absolute',
  left,
  top,
  width,
  height,
  borderRadius: 1,
  border: `1px solid ${Colors.gray6}`,
  backgroundColor: Colors.gray2,
  overflow: 'hidden',
});

const windowSx = ({
  left,
  top,
  width,
  height,
  color,
  renderedWidth,
}: {
  left: number;
  top: number;
  width: number;
  height: number;
  color: string;
  renderedWidth: number;
}): SxProps<Theme> => ({
  position: 'absolute',
  left,
  top,
  width,
  height,
  px: 0.5,
  borderRadius: 0.5,
  border: `1px solid ${transparentize(color, 0.25)}`,
  backgroundColor: transparentize(color, 0.2),
  color: Colors.gray10,
  fontSize: renderedWidth < 72 ? 9 : 11,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const legendSx: SxProps<Theme> = {
  mt: 1,
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 1.25,
};

const legendItemSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: 12,
  color: Colors.gray8,
};

const legendSwatchSx = (screen: ScreenName): SxProps<Theme> => ({
  width: 10,
  height: 10,
  borderRadius: 0.5,
  backgroundColor: getWindowColor(screen),
});

export const WorkspaceLayoutPreview = ({ workspaceLayoutPreset }: WorkspaceLayoutPreviewProps) => {
  const [resizeListener, sizes] = useResizeAware();

  const displays = [...workspaceLayoutPreset.displaySlots].sort((a, b) => a.index - b.index);
  const windows = workspaceLayoutPreset.windows;

  if (displays.length === 0) {
    return null;
  }

  const arrangement = getArrangementBounds(displays);

  const measuredWidth = sizes?.width ?? 0;
  const canvasWidth = Math.max(measuredWidth, CANVAS_MIN_WIDTH);

  const canvas = getCanvasDimensions({
    arrangement,
    canvasMaxWidth: canvasWidth,
    canvasVerticalPadding: CANVAS_VERTICAL_PADDING,
  });

  const windowsByDisplayIndex = groupWindowsByDisplayIndex(windows);
  const visibleScreens = Array.from(new Set(windows.map((window) => window.screen)));

  return (
    <Box sx={outerContainerSx}>
      {resizeListener}

      {measuredWidth > 0 && (
        <>
          <Box sx={displayContainerSx(canvas.width, canvas.height)}>
            {displays.map((display) => {
              const geometry = getDisplayGeometry({
                display,
                arrangement,
                canvas,
                canvasVerticalPadding: CANVAS_VERTICAL_PADDING,
                displayGap: DISPLAY_GAP,
              });

              const displayWindows = windowsByDisplayIndex.get(display.index) ?? [];

              return (
                <Box key={display.index} sx={displaySx(geometry)}>
                  {displayWindows.map((window) => {
                    const color = getWindowColor(window.screen);
                    const renderedWidth = geometry.width * window.relW;
                    const { label, displayLabel } = getWindowLabel({
                      screen: window.screen,
                      renderedWidth,
                    });

                    const windowGeometry = resolveRelativeRect(
                      {
                        left: 0,
                        top: 0,
                        width: geometry.width,
                        height: geometry.height,
                      },
                      {
                        relX: window.relX,
                        relY: window.relY,
                        relW: window.relW,
                        relH: window.relH,
                      }
                    );

                    return (
                      <Box
                        key={window.id}
                        title={`${label} • ${window.type}`}
                        sx={windowSx({
                          left: windowGeometry.left,
                          top: windowGeometry.top,
                          width: windowGeometry.width,
                          height: windowGeometry.height,
                          color,
                          renderedWidth,
                        })}
                      >
                        {displayLabel}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          <Box sx={legendSx}>
            {visibleScreens.map((screen) => {
              const label = getWindowName(screen);

              return (
                <Box key={screen} sx={legendItemSx}>
                  <Box sx={legendSwatchSx(screen)} />
                  {label}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};
